import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Row, Col } from 'react-flexbox-grid';
import { useDispatch, useSelector } from 'react-redux';
import { RiCheckboxBlankLine, RiCheckboxFill, RiDeleteBin2Line, RiAddLine } from 'react-icons/ri';
import ContentLoader from 'react-content-loader';

import TitlePage from '@/components/TitlePage';
import Card from '@/components/Card';
import DataTable from '@/components/DataTable';
import Input from '@/components/Forms/Input';
import SelectOne from '@/components/Forms/SelectOne';
import Switcher from '@/components/Forms/Switcher';
import InputMoney from '@/components/Forms/InputMoney';
import InputPercentage from '@/components/Forms/InputPercentage';

import { colors } from '@/template/styles';
import { showAlert } from '@/store/actions/theme';
import { translate } from '@/i18n/src/locales';
import { apiLoja } from '@/services/api';

import {
  fetchProgressiveDiscountById,
  addProgressiveDiscount,
  editProgressiveDiscount,
  clearProgressiveDiscount,
  fetchProgressiveDiscountTypes,
} from '@/store/actions/progressiveDiscount';
import * as S from './styles';

function ProgressiveDiscountCreate() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isEdit = !!id;

  // Redux States
  const discountEdit = useSelector((state) => state.progressiveDiscount?.discountEdit);
  const discountIsLoading = useSelector((state) => state.progressiveDiscount?.discountIsLoading);
  const discountTypes = useSelector((state) => state.progressiveDiscount?.discountTypes) || [];
  const discountTypesIsLoading = useSelector((state) => state.progressiveDiscount?.discountTypesIsLoading);
  const IdFranqueadora = useSelector((state) => state.user?.user?.IdFranqueadora);

  // Form Fields State
  const [name, setName] = useState('');
  const [nameStatus, setNameStatus] = useState({ status: '', feedback: '' });

  const [type, setType] = useState(null);
  const [typeStatus, setTypeStatus] = useState({ status: '', feedback: '' });

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateFromApi = (dateStr) => {
    if (!dateStr) return '';
    const dateOnly = dateStr.split('T')[0];
    if (dateOnly.startsWith('0001-') || dateOnly.startsWith('1900-')) {
      return '';
    }
    return dateOnly;
  };

  const [initialDate, setInitialDate] = useState(getTodayDate);
  const [initialDateStatus, setInitialDateStatus] = useState({ status: '', feedback: '' });

  const [endDate, setEndDate] = useState('');
  const [endDateStatus, setEndDateStatus] = useState({ status: '', feedback: '' });

  const [discountIsPercentage, setDiscountIsPercentage] = useState(true); // global: true = %, false = R$
  const [active, setActive] = useState(true);

  // Helper to create a default Faixa
  const createNewFaixa = () => ({
    id: `faixa_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    discountValue: '',
    discountStatus: { status: '', feedback: '' },
    minQuantity: '',
    minQuantityStatus: { status: '', feedback: '' },
    hasMaxQuantity: false,
    maxQuantity: '',
    maxQuantityStatus: { status: '', feedback: '' },
  });

  // Dynamic Faixas State (starts with 1 initial Faixa)
  const [faixas, setFaixas] = useState(() => [createNewFaixa()]);

  const handleAddFaixa = () => {
    setFaixas((prev) => [...prev, createNewFaixa()]);
  };

  const handleRemoveFaixa = (faixaId) => {
    setFaixas((prev) => prev.filter((f) => f.id !== faixaId));
  };

  const handleFaixaChange = (faixaId, field, value) => {
    setFaixas((prev) =>
      prev.map((f) => {
        if (f.id === faixaId) {
          const updated = { ...f, [field]: value };
          if (field === 'hasMaxQuantity' && !value) {
            updated.maxQuantity = '';
            updated.maxQuantityStatus = { status: '', feedback: '' };
          }
          if (field === 'discountValue') updated.discountStatus = { status: '', feedback: '' };
          if (field === 'minQuantity') updated.minQuantityStatus = { status: '', feedback: '' };
          if (field === 'maxQuantity') updated.maxQuantityStatus = { status: '', feedback: '' };
          return updated;
        }
        return f;
      })
    );
  };

  const handleDiscountIsPercentageChange = (isPercentage) => {
    setDiscountIsPercentage(isPercentage);
    setFaixas((prev) =>
      prev.map((f) => ({
        ...f,
        discountStatus: { status: '', feedback: '' },
      }))
    );
  };

  // Dynamic Type Options mapped from Redux
  const [typeOptions, setTypeOptions] = useState([]);

  // Products list state and loading indicator
  const [productsList, setProductsList] = useState([]);
  const [productsIsLoading, setProductsIsLoading] = useState(false);

  // Calcular o maior desconto configurado nas faixas quando o desconto for em Real
  const maxDiscountBrl = React.useMemo(() => {
    if (discountIsPercentage) return 0;
    let maxDiscount = 0;
    faixas.forEach((f) => {
      const val = Number((f.discountValue || 0).toString().replace(',', '.'));
      if (!isNaN(val) && val > maxDiscount) {
        maxDiscount = val;
      }
    });
    return maxDiscount;
  }, [discountIsPercentage, faixas]);

  // Efeito para remover a seleção dos itens que possuem preço menor que o desconto progressivo
  useEffect(() => {
    if (!discountIsPercentage) {
      setProductsList((prev) => {
        let changed = false;
        const updated = prev.map((p) => {
          const price = Number(p.PrecoPadrao || 0);
          if (price < maxDiscountBrl && p.Vinculado) {
            changed = true;
            return { ...p, Vinculado: false };
          }
          return p;
        });
        return changed ? updated : prev;
      });
    }
  }, [discountIsPercentage, maxDiscountBrl]);

  // Efeito para validar faixas duplicadas "a quente" (em tempo real)
  useEffect(() => {
    setFaixas((prevFaixas) => {
      let hasChanges = false;
      const updated = prevFaixas.map((faixa) => {
        const minQtyNum = Number(faixa.minQuantity);

        const isDuplicate = prevFaixas.some((other) => {
          if (other.id === faixa.id) return false;

          const otherMinQtyNum = Number(other.minQuantity);
          if (isNaN(minQtyNum) || isNaN(otherMinQtyNum) || !faixa.minQuantity || !other.minQuantity || minQtyNum !== otherMinQtyNum) {
            return false;
          }

          if (faixa.hasMaxQuantity !== other.hasMaxQuantity) {
            return false;
          }

          if (faixa.hasMaxQuantity) {
            const maxQtyNum = Number(faixa.maxQuantity);
            const otherMaxQtyNum = Number(other.maxQuantity);
            if (!faixa.maxQuantity || !other.maxQuantity || isNaN(maxQtyNum) || isNaN(otherMaxQtyNum)) {
              return false;
            }
            return maxQtyNum === otherMaxQtyNum;
          }

          return true;
        });

        let newMinStatus = { ...faixa.minQuantityStatus };
        let newMaxStatus = { ...faixa.maxQuantityStatus };
        const errMsg = translate('duplicateFaixaRange') || 'Não é permitido ter faixas com o mesmo intervalo.';

        if (isDuplicate) {
          if (faixa.minQuantityStatus.status !== 'danger' || faixa.minQuantityStatus.feedback !== errMsg) {
            newMinStatus = { status: 'danger', feedback: errMsg };
            hasChanges = true;
          }
          if (faixa.hasMaxQuantity && (faixa.maxQuantityStatus.status !== 'danger' || faixa.maxQuantityStatus.feedback !== errMsg)) {
            newMaxStatus = { status: 'danger', feedback: errMsg };
            hasChanges = true;
          }
        } else {
          if (faixa.minQuantityStatus.feedback === errMsg) {
            newMinStatus = { status: '', feedback: '' };
            hasChanges = true;
          }
          if (faixa.maxQuantityStatus.feedback === errMsg) {
            newMaxStatus = { status: '', feedback: '' };
            hasChanges = true;
          }
        }

        if (hasChanges) {
          return {
            ...faixa,
            minQuantityStatus: newMinStatus,
            maxQuantityStatus: newMaxStatus,
          };
        }
        return faixa;
      });

      return hasChanges ? updated : prevFaixas;
    });
  }, [
    JSON.stringify(
      faixas.map((f) => ({
        min: f.minQuantity,
        max: f.maxQuantity,
        hasMax: f.hasMaxQuantity,
      }))
    ),
  ]);

  // 1. Fetch Types Options from Redux
  useEffect(() => {
    dispatch(fetchProgressiveDiscountTypes());
  }, [dispatch]);

  // Sync types array to select options
  useEffect(() => {
    if (discountTypes && discountTypes.length > 0) {
      setTypeOptions(
        discountTypes.map((item) => ({
          label: item.Nome || item.Descricao,
          value: String(item.IdDescontoProgressivoTipo),
        }))
      );
    }
  }, [discountTypes]);

  // 2. Fetch products by Franqueadora in creation mode
  useEffect(() => {
    if (!isEdit && IdFranqueadora) {
      setProductsIsLoading(true);
      apiLoja
        .get(`/DescontoProgressivo/GetProdutoDescontoProgressivoByIdFranqueadora/${IdFranqueadora}`)
        .then((res) => {
          if (res.data && Array.isArray(res.data)) {
            const mapped = res.data.map((p, index) => {
              const prodId = Number(p.IdProduto !== undefined ? p.IdProduto : (p.idProduto !== undefined ? p.idProduto : p.id));
              const secaoId = Number(p.IdSecao !== undefined ? p.IdSecao : (p.idSecao !== undefined ? p.idSecao : 0));

              const discountObj = p.DescontoProgressivo || p.descontoProgressivo || null;
              const pDiscId = discountObj
                ? Number(discountObj.IdDescontoProgressivo !== undefined ? discountObj.IdDescontoProgressivo : (discountObj.idDescontoProgressivo || 0))
                : Number(p.IdDescontoProgressivo !== undefined ? p.IdDescontoProgressivo : (p.idDescontoProgressivo || 0));
              const discountName = discountObj
                ? (discountObj.Nome || discountObj.nome || '')
                : (p.NomeDescontoProgressivo || p.nomeDescontoProgressivo || '');

              const isAllowedToEdit = !pDiscId || pDiscId === 0;
              const rowId = `prod_${prodId}_secao_${secaoId}_idx_${index}`;

              return {
                ...p,
                id: rowId,
                _rowId: rowId,
                IdProduto: prodId,
                Nome: p.Nome || p.nome || '',
                IdSecao: secaoId,
                NomeSecao: p.NomeSecao || p.nomeSecao || '',
                NomeDescontoProgressivo: discountName,
                Imagem: p.Imagem || p.imagem || p.ImagemBase64 || '',
                IdDescontoProgressivo: pDiscId,
                Vinculado: false,
                isAllowedToEdit,
              };
            });
            mapped.sort((a, b) => (b.Vinculado ? 1 : 0) - (a.Vinculado ? 1 : 0));
            setProductsList(mapped);
          } else {
            setProductsList([]);
          }
          setProductsIsLoading(false);
        })
        .catch((err) => {
          console.log('Error fetching products by franqueadora', err);
          setProductsList([]);
          setProductsIsLoading(false);
        });
    }
  }, [isEdit, IdFranqueadora]);

  // 2B. Fetch products in edit mode when discountEdit changes (handles initial load and post-save refresh)
  useEffect(() => {
    if (isEdit && discountEdit && IdFranqueadora) {
      setProductsIsLoading(true);
      apiLoja
        .get(`/DescontoProgressivo/GetProdutoDescontoProgressivoByIdFranqueadora/${IdFranqueadora}`)
        .then((res) => {
          if (res.data && Array.isArray(res.data)) {
            const mapped = res.data.map((p, index) => {
              const prodId = Number(p.IdProduto !== undefined ? p.IdProduto : (p.idProduto !== undefined ? p.idProduto : p.id));
              const secaoId = Number(p.IdSecao !== undefined ? p.IdSecao : (p.idSecao !== undefined ? p.idSecao : 0));

              const discountObj = p.DescontoProgressivo || p.descontoProgressivo || null;
              const pDiscId = discountObj
                ? Number(discountObj.IdDescontoProgressivo !== undefined ? discountObj.IdDescontoProgressivo : (discountObj.idDescontoProgressivo || 0))
                : Number(p.IdDescontoProgressivo !== undefined ? p.IdDescontoProgressivo : (p.idDescontoProgressivo || 0));
              const discountName = discountObj
                ? (discountObj.Nome || discountObj.nome || '')
                : (p.NomeDescontoProgressivo || p.nomeDescontoProgressivo || '');

              const currentEditId = Number(id) || Number(discountEdit.IdDescontoProgressivo) || 0;
              const isCurrentDiscount = currentEditId > 0 && pDiscId === currentEditId;
              const isAllowedToEdit = !pDiscId || pDiscId === 0 || isCurrentDiscount;
              const isLinked = isCurrentDiscount || !!p.Vinculado;
              const rowId = `prod_${prodId}_secao_${secaoId}_idx_${index}`;

              return {
                ...p,
                id: rowId,
                _rowId: rowId,
                IdProduto: prodId,
                Nome: p.Nome || p.nome || '',
                IdSecao: secaoId,
                NomeSecao: p.NomeSecao || p.nomeSecao || '',
                NomeDescontoProgressivo: discountName,
                Imagem: p.Imagem || p.imagem || p.ImagemBase64 || '',
                IdDescontoProgressivo: pDiscId,
                Vinculado: isLinked,
                isAllowedToEdit,
              };
            });
            mapped.sort((a, b) => (b.Vinculado ? 1 : 0) - (a.Vinculado ? 1 : 0));
            setProductsList(mapped);
          } else {
            setProductsList([]);
          }
          setProductsIsLoading(false);
        })
        .catch((err) => {
          console.log('Error fetching products by franqueadora', err);
          setProductsList([]);
          setProductsIsLoading(false);
        });
    }
  }, [isEdit, discountEdit, IdFranqueadora, id]);

  // 3. Fetch details if edit mode
  useEffect(() => {
    if (isEdit) {
      dispatch(fetchProgressiveDiscountById(id));
    }

    return () => {
      dispatch(clearProgressiveDiscount());
    };
  }, [id, isEdit, dispatch]);

  // 4. Sync Redux state discountEdit to Local Fields and map received products
  useEffect(() => {
    if (isEdit && discountEdit) {
      setName(discountEdit.Nome || '');
      setInitialDate(parseDateFromApi(discountEdit.PeriodoInicial));
      setEndDate(parseDateFromApi(discountEdit.PeriodoFinal));
      setActive(discountEdit.Ativo !== undefined ? !!discountEdit.Ativo : true);

      // Populate discountIsPercentage globally
      const rawFaixas = discountEdit.Faixas || discountEdit.faixas || [];
      let isGlobalPct = true;
      if (discountEdit.DescontoPorcentagem !== undefined) {
        isGlobalPct = !!discountEdit.DescontoPorcentagem;
      } else if (rawFaixas.length > 0 && rawFaixas[0].DescontoPorcentagem !== undefined) {
        isGlobalPct = !!rawFaixas[0].DescontoPorcentagem;
      }
      setDiscountIsPercentage(isGlobalPct);

      if (Array.isArray(rawFaixas) && rawFaixas.length > 0) {
        const mappedFaixas = rawFaixas.map((f, idx) => {
          const valDesc = f.ValorDesconto !== undefined ? f.ValorDesconto : (f.valorDesconto !== undefined ? f.valorDesconto : '');
          const minQ = f.QuantidadeMinima !== undefined ? f.QuantidadeMinima : (f.quantidadeMinima !== undefined ? f.quantidadeMinima : '');
          const maxQ = f.QuantidadeMaxima !== undefined ? f.QuantidadeMaxima : (f.quantidadeMaxima !== undefined ? f.quantidadeMaxima : 0);
          const hasMax = Number(maxQ) > 0;

          let valDescStr = '';
          if (valDesc !== '' && valDesc !== null && valDesc !== undefined) {
            const numVal = Number(valDesc);
            if (!isNaN(numVal)) {
              valDescStr = numVal.toFixed(2).replace('.', ',');
            }
          }

          return {
            id: `faixa_${idx}_${Date.now()}`,
            discountValue: valDescStr,
            discountStatus: { status: '', feedback: '' },
            minQuantity: minQ !== '' && minQ !== null ? String(minQ) : '',
            minQuantityStatus: { status: '', feedback: '' },
            hasMaxQuantity: hasMax,
            maxQuantity: hasMax ? String(maxQ) : '',
            maxQuantityStatus: { status: '', feedback: '' },
          };
        });
        setFaixas(mappedFaixas);
      } else if (
        (discountEdit.QuantidadeMinima !== undefined && discountEdit.QuantidadeMinima !== null && discountEdit.QuantidadeMinima !== '') ||
        (discountEdit.ValorDesconto !== undefined && discountEdit.ValorDesconto !== null && discountEdit.ValorDesconto !== '')
      ) {
        const valDesc = discountEdit.ValorDesconto !== undefined ? discountEdit.ValorDesconto : '';
        const minQ = discountEdit.QuantidadeMinima !== undefined ? discountEdit.QuantidadeMinima : '';
        const maxQ = discountEdit.QuantidadeMaxima !== undefined ? discountEdit.QuantidadeMaxima : 0;
        const hasMax = Number(maxQ) > 0;

        let valDescStr = '';
        if (valDesc !== '' && valDesc !== null && valDesc !== undefined) {
          const numVal = Number(valDesc);
          if (!isNaN(numVal)) {
            valDescStr = numVal.toFixed(2).replace('.', ',');
          }
        }

        setFaixas([
          {
            id: `faixa_legacy_${Date.now()}`,
            discountValue: valDescStr,
            discountStatus: { status: '', feedback: '' },
            minQuantity: minQ !== '' && minQ !== null ? String(minQ) : '',
            minQuantityStatus: { status: '', feedback: '' },
            hasMaxQuantity: hasMax,
            maxQuantity: hasMax ? String(maxQ) : '',
            maxQuantityStatus: { status: '', feedback: '' },
          },
        ]);
      } else {
        setFaixas([createNewFaixa()]);
      }

      // Match type option
      if (typeOptions.length > 0 && !type) {
        const matchedType = typeOptions.find(
          (opt) => Number(opt.value) === discountEdit.IdDescontoProgressivoTipo
        );
        setType(matchedType || null);
      }
    }
  }, [isEdit, discountEdit, id]);

  // Helper function to check if a product can be selected/unselected
  const getIsAllowedToEdit = (product) => {
    if (!discountIsPercentage && Number(product.PrecoPadrao || 0) < maxDiscountBrl) {
      return false;
    }

    const currentEditId = isEdit ? (Number(id) || (discountEdit ? Number(discountEdit.IdDescontoProgressivo) : 0)) : 0;

    const discountObj = product.DescontoProgressivo || product.descontoProgressivo || null;
    const pDiscId = discountObj
      ? Number(discountObj.IdDescontoProgressivo !== undefined ? discountObj.IdDescontoProgressivo : (discountObj.idDescontoProgressivo || 0))
      : (product.IdDescontoProgressivo !== undefined && product.IdDescontoProgressivo !== null
        ? Number(product.IdDescontoProgressivo)
        : (product.idDescontoProgressivo !== undefined && product.idDescontoProgressivo !== null ? Number(product.idDescontoProgressivo) : 0));

    if (!pDiscId || pDiscId === 0) return true;
    if (isEdit && currentEditId > 0 && pDiscId === currentEditId) return true;
    return false;
  };

  // Toggle linkage for a product by IdProduto, IdSecao, and unique row key
  const handleProductToggleLink = (idProduto, idSecao, targetRowId) => {
    const targetProdId = Number(idProduto);
    const targetSecaoId = Number(idSecao);

    setProductsList((prev) =>
      prev.map((p) => {
        const prodId = Number(p.IdProduto !== undefined ? p.IdProduto : (p.idProduto !== undefined ? p.idProduto : p.id));
        const secaoId = Number(p.IdSecao !== undefined ? p.IdSecao : (p.idSecao !== undefined ? p.idSecao : 0));

        const isMatch = targetRowId
          ? p._rowId === targetRowId
          : (prodId === targetProdId && secaoId === targetSecaoId);

        if (isMatch) {
          const isAllowedToEdit = getIsAllowedToEdit(p);
          if (!isAllowedToEdit) {
            return p;
          }
          return { ...p, Vinculado: !p.Vinculado };
        }
        return p;
      })
    );
  };

  // Handle Form Submit and Validation
  const handleSave = () => {
    let hasError = false;

    // Reset validations
    setNameStatus({ status: '', feedback: '' });
    setTypeStatus({ status: '', feedback: '' });
    setInitialDateStatus({ status: '', feedback: '' });
    setEndDateStatus({ status: '', feedback: '' });

    if (!name.trim()) {
      setNameStatus({ status: 'danger', feedback: 'Nome em branco' });
      hasError = true;
    }

    if (!type) {
      setTypeStatus({ status: 'danger', feedback: 'O tipo do desconto é obrigatório' });
      hasError = true;
    }

    if (!initialDate) {
      setInitialDateStatus({ status: 'danger', feedback: 'Período inicial é obrigatório' });
      hasError = true;
    }

    if (endDate) {
      const finalDateObj = new Date(endDate + 'T23:59:59');
      const initialDateObj = initialDate ? new Date(initialDate + 'T00:00:00') : null;
      if (finalDateObj < new Date() || (initialDateObj && finalDateObj < initialDateObj)) {
        setEndDateStatus({
          status: 'danger',
          feedback: 'O período final deve ser maior que a data atual e do período inicial',
        });
        hasError = true;
      }
    }

    // Validate faixas
    const updatedFaixas = faixas.map((faixa) => {
      let faixaHasError = false;
      let discountStatus = { status: '', feedback: '' };
      let minQuantityStatus = { status: '', feedback: '' };
      let maxQuantityStatus = { status: '', feedback: '' };

      const discountNum = Number((faixa.discountValue || '').toString().replace(',', '.'));
      if (!faixa.discountValue || isNaN(discountNum) || discountNum <= 0) {
        discountStatus = { status: 'danger', feedback: 'Desconto não pode ser menor ou igual a 0' };
        faixaHasError = true;
      } else if (discountIsPercentage && discountNum > 100) {
        discountStatus = { status: 'danger', feedback: 'Desconto não pode ser maior que 100' };
        faixaHasError = true;
      }

      const minQtyNum = Number(faixa.minQuantity);
      if (!faixa.minQuantity || isNaN(minQtyNum) || minQtyNum <= 0) {
        minQuantityStatus = { status: 'danger', feedback: 'Quantidade mínima não pode ser menor ou igual a 0' };
        faixaHasError = true;
      }

      if (faixa.hasMaxQuantity) {
        const maxQtyNum = Number(faixa.maxQuantity);
        if (!faixa.maxQuantity || isNaN(maxQtyNum) || maxQtyNum <= 0 || maxQtyNum <= minQtyNum) {
          maxQuantityStatus = {
            status: 'danger',
            feedback: 'A quantidade máxima deve ser maior que a quantidade mínima',
          };
          faixaHasError = true;
        }
      }

      // Verificar se existem outras faixas com o mesmo intervalo/range
      const isDuplicate = faixas.some((other) => {
        if (other.id === faixa.id) return false;

        const otherMinQtyNum = Number(other.minQuantity);
        if (isNaN(minQtyNum) || isNaN(otherMinQtyNum) || minQtyNum !== otherMinQtyNum) {
          return false;
        }

        if (faixa.hasMaxQuantity !== other.hasMaxQuantity) {
          return false;
        }

        if (faixa.hasMaxQuantity) {
          const maxQtyNum = Number(faixa.maxQuantity);
          const otherMaxQtyNum = Number(other.maxQuantity);
          return !isNaN(maxQtyNum) && !isNaN(otherMaxQtyNum) && maxQtyNum === otherMaxQtyNum;
        }

        return true;
      });

      if (isDuplicate) {
        minQuantityStatus = {
          status: 'danger',
          feedback: translate('duplicateFaixaRange') || 'Não é permitido ter faixas com o mesmo intervalo.',
        };
        if (faixa.hasMaxQuantity) {
          maxQuantityStatus = {
            status: 'danger',
            feedback: translate('duplicateFaixaRange') || 'Não é permitido ter faixas com o mesmo intervalo.',
          };
        }
        faixaHasError = true;
      }

      if (faixaHasError) {
        hasError = true;
      }

      return {
        ...faixa,
        discountStatus,
        minQuantityStatus,
        maxQuantityStatus,
      };
    });

    setFaixas(updatedFaixas);

    if (hasError) {
      dispatch(showAlert({ type: 'danger', message: 'Por favor, preencha os campos obrigatórios corretamente.', time: 4000 }));
      return;
    }

    const payload = {
      Nome: name,
      IdDescontoProgressivoTipo: Number(type.value),
      PeriodoInicial: initialDate + 'T00:00:00',
      PeriodoFinal: endDate ? endDate + 'T00:00:00' : null,
      DescontoPorcentagem: discountIsPercentage,
      Ativo: active,
      IdFranqueadora: Number(IdFranqueadora),
      Faixas: faixas.map((f) => ({
        DescontoPorcentagem: !!discountIsPercentage,
        ValorDesconto: Number((f.discountValue || 0).toString().replace(',', '.')),
        QuantidadeMinima: Number(f.minQuantity || 0),
        QuantidadeMaxima: f.hasMaxQuantity ? Number(f.maxQuantity || 0) : 0,
      })),
      Produtos: productsList
        .filter((p) => !!p.Vinculado)
        .map((p) => {
          const prodId = Number(p.IdProduto !== undefined ? p.IdProduto : (p.idProduto !== undefined ? p.idProduto : p.id));
          const secaoId = Number(p.IdSecao !== undefined ? p.IdSecao : (p.idSecao !== undefined ? p.idSecao : 0));
          const currentDiscId = Number(id) || (discountEdit ? Number(discountEdit.IdDescontoProgressivo) : 0);

          return {
            IdProduto: prodId,
            Nome: p.Nome || p.nome || '',
            IdSecao: secaoId,
            NomeSecao: p.NomeSecao || p.nomeSecao || '',
            Imagem: p.Imagem || p.imagem || p.ImagemBase64 || '',
            IdDescontoProgressivo: currentDiscId || Number(p.IdDescontoProgressivo || 0),
            NomeDescontoProgressivo: p.NomeDescontoProgressivo || p.nomeDescontoProgressivo || '',
          };
        }),
    };

    if (isEdit) {
      payload.IdDescontoProgressivo = Number(id);
      dispatch(editProgressiveDiscount(payload));
    } else {
      dispatch(addProgressiveDiscount(payload));
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Administração Franqueadora - {isEdit ? translate('progressiveDiscountEdit') : translate('progressiveDiscountCreate')}
        </title>
      </Helmet>
      <TitlePage title={isEdit ? translate('progressiveDiscountEdit') : translate('progressiveDiscountCreate')} />
      <Card>
        {discountIsLoading ? (
          <ContentLoader width="100%" speed={1} height="380px" backgroundColor="#c6c6c6" foregroundColor="#F0F0F5">
            <rect x="0" y="0" rx="5px" ry="5px" width="100%" height="40px" />
            <rect x="0" y="60" rx="5px" ry="5px" width="100%" height="40px" />
            <rect x="0" y="120" rx="5px" ry="5px" width="100%" height="40px" />
            <rect x="0" y="180" rx="5px" ry="5px" width="100%" height="40px" />
            <rect x="0" y="240" rx="5px" ry="5px" width="100%" height="40px" />
          </ContentLoader>
        ) : (
          <S.Container>
            <Row style={{ marginBottom: '1.5rem' }}>
              <Col xs={12} md={3}>
                <S.SwitcherWrapper>
                  <Switcher
                    set={setActive}
                    value={active}
                    label={translate('activateOrDeactivateDiscount') || 'Ativar ou Desativar Desconto'}
                  />
                </S.SwitcherWrapper>
              </Col>
              <Col xs={12} md={5}>
                <Input
                  name="name"
                  type="text"
                  label={translate('progressiveDiscountName') || 'Nome do Desconto Progressivo'}
                  value={name}
                  change={(val) => setName(val)}
                  status={nameStatus}
                />
              </Col>
              <Col xs={12} md={4}>
                <SelectOne
                  options={typeOptions}
                  value={type}
                  change={(val) => setType(val)}
                  label={translate('progressiveDiscountType') || 'Tipo de Desconto Progressivo'}
                  placeholder={translate('selectType') || 'Selecione o tipo...'}
                  status={typeStatus}
                  isDisabled={discountTypesIsLoading}
                />
              </Col>
            </Row>

            <Row style={{ marginBottom: '1.5rem' }}>
              <Col xs={12} md={4}>
                <Input
                  name="initial-date"
                  type="date"
                  label={translate('initialPeriod') || 'Período Inicial'}
                  value={initialDate}
                  change={(val) => setInitialDate(val)}
                  status={initialDateStatus}
                />
              </Col>
              <Col xs={12} md={4}>
                <Input
                  name="end-date"
                  type="date"
                  label={translate('endPeriod') || 'Período Final'}
                  value={endDate}
                  change={(val) => setEndDate(val)}
                  status={endDateStatus}
                />
              </Col>
              <Col xs={12} md={4}>
                <S.TypeSelectorGroup>
                  <S.TypeSelectorLabel>
                    {translate('discountType') || 'Tipo do Desconto'}
                  </S.TypeSelectorLabel>
                  <S.TypeSegmentContainer>
                    <S.TypeSegmentButton
                      type="button"
                      active={discountIsPercentage}
                      onClick={() => handleDiscountIsPercentageChange(true)}
                    >
                      % Porcentagem
                    </S.TypeSegmentButton>
                    <S.TypeSegmentButton
                      type="button"
                      active={!discountIsPercentage}
                      onClick={() => handleDiscountIsPercentageChange(false)}
                    >
                      R$ Real
                    </S.TypeSegmentButton>
                  </S.TypeSegmentContainer>
                </S.TypeSelectorGroup>
              </Col>
            </Row>

            <S.SectionTitle>{translate('quantityRange') || 'Faixas de Quantidade'}</S.SectionTitle>

            <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '1.5rem', marginTop: '-0.5rem', lineHeight: '1.4' }}>
              {translate('discountRangePriceAlert') || 'Produtos com preço menor que o valor do desconto não poderão ser vinculados.'}
            </p>

            {faixas.length === 0 ? (
              <S.EmptyFaixasNotice>
                {translate('noFaixasConfigured') || 'Nenhuma faixa de quantidade configurada. Clique no botão abaixo para incluir uma faixa.'}
              </S.EmptyFaixasNotice>
            ) : (
              faixas.map((faixa, index) => (
                <S.FaixaCard key={faixa.id}>
                  <S.FaixaHeader>
                    <S.FaixaTitle>
                      {translate('faixa') || 'Faixa'} {index + 1}
                    </S.FaixaTitle>
                    <S.RemoveFaixaButton
                      type="button"
                      onClick={() => handleRemoveFaixa(faixa.id)}
                    >
                      <RiDeleteBin2Line size={18} />
                      {translate('removeFaixa') || 'Remover'}
                    </S.RemoveFaixaButton>
                  </S.FaixaHeader>

                  <Row style={{ alignItems: 'flex-start' }}>
                    <Col xs={12} md={4}>
                      {discountIsPercentage ? (
                        <InputPercentage
                          name={`discount-value-${faixa.id}`}
                          value={faixa.discountValue}
                          label={translate('discountValuePercentage') || 'Valor do Desconto (%)'}
                          status={faixa.discountStatus}
                          change={(val) => handleFaixaChange(faixa.id, 'discountValue', val)}
                        />
                      ) : (
                        <InputMoney
                          name={`discount-value-${faixa.id}`}
                          value={faixa.discountValue}
                          label={translate('discountValueBrl') || 'Valor do Desconto (R$)'}
                          status={faixa.discountStatus}
                          change={(val) => handleFaixaChange(faixa.id, 'discountValue', val)}
                        />
                      )}
                    </Col>

                    <Col xs={12} md={4}>
                      <Input
                        name={`min-qty-${faixa.id}`}
                        type="number"
                        label={translate('minQuantity') || 'Quantidade Mínima'}
                        value={faixa.minQuantity}
                        change={(val) => handleFaixaChange(faixa.id, 'minQuantity', val)}
                        status={faixa.minQuantityStatus}
                      />
                    </Col>

                    <Col xs={12} md={4}>
                      <S.SwitcherInputGroup>
                        <div className="switcher-container">
                          <Switcher
                            set={(val) => handleFaixaChange(faixa.id, 'hasMaxQuantity', val)}
                            value={faixa.hasMaxQuantity}
                            label={translate('defineMaxQty') || 'Definir Qtd Máxima?'}
                          />
                        </div>
                        <div className="input-container">
                          <Input
                            name={`max-qty-${faixa.id}`}
                            type="number"
                            label={translate('maxQuantity') || 'Quantidade Máxima'}
                            value={faixa.maxQuantity}
                            change={(val) => handleFaixaChange(faixa.id, 'maxQuantity', val)}
                            status={faixa.maxQuantityStatus}
                            disabled={!faixa.hasMaxQuantity}
                          />
                        </div>
                      </S.SwitcherInputGroup>
                    </Col>
                  </Row>
                </S.FaixaCard>
              ))
            )}

            <div style={{ marginTop: '1rem', marginBottom: '3.5rem', display: 'flex', justifyContent: 'flex-start' }}>
              <S.AddFaixaButton type="button" onClick={handleAddFaixa}>
                <RiAddLine size={18} />
                {translate('addFaixa') || 'Adicionar Faixa'}
              </S.AddFaixaButton>
            </div>


            <S.SectionTitle>{translate('linkProducts') || 'Vincular Produtos'}</S.SectionTitle>
            <div style={{ marginBottom: '1.5rem' }}>
              <DataTable
                data={productsList}
                searchPlaceHolder={translate('searchProducts') || 'Buscar produtos'}
                defaultSortField="Vinculado"
                defaultSortAsc={false}
                columns={[
                  {
                    compact: true,
                    center: true,
                    width: '80px',
                    cell: (row) => row.Imagem && <img src={row.Imagem} alt={row.Nome} style={{ width: '40px', height: '40px', borderRadius: '4px' }} />,
                  },
                  ...(Number(IdFranqueadora) === 48 ? [] : [
                    {
                      name: translate('idProduct') || 'Id Produto',
                      selector: 'IdProduto',
                      sortable: true,
                      width: '110px',
                    }
                  ]),
                  {
                    name: translate('name') || 'Nome',
                    selector: 'Nome',
                    sortable: true,
                  },
                  ...(Number(IdFranqueadora) === 48 ? [] : [
                    {
                      name: translate('idSection') || 'Id Seção',
                      selector: 'IdSecao',
                      sortable: true,
                      width: '100px',
                    }
                  ]),
                  {
                    name: translate('section') || 'Seção',
                    selector: 'NomeSecao',
                    sortable: true,
                  },
                  {
                    name: translate('price') || 'Preço',
                    selector: 'PrecoPadrao',
                    sortable: true,
                    width: '110px',
                    cell: (row) => {
                      const price = Number(row.PrecoPadrao || 0);
                      return `R$ ${price.toFixed(2).replace('.', ',')}`;
                    }
                  },
                  {
                    name: translate('progressiveDiscount') || 'Desconto Progressivo',
                    selector: 'NomeDescontoProgressivo',
                    sortable: true,
                    cell: (row) => {
                      const pDiscId = row.IdDescontoProgressivo ? Number(row.IdDescontoProgressivo) : 0;
                      const currentEditId = Number(id) || (discountEdit ? Number(discountEdit.IdDescontoProgressivo) : 0);

                      if (row.Vinculado || (currentEditId > 0 && pDiscId === currentEditId)) {
                        return name || row.NomeDescontoProgressivo || row.nomeDescontoProgressivo || '-';
                      }

                      if (pDiscId > 0) {
                        return row.NomeDescontoProgressivo || row.nomeDescontoProgressivo || '-';
                      }

                      return '-';
                    },
                  },
                  {
                    name: translate('Ativo') || 'Ativo',
                    selector: 'Vinculado',
                    sortable: true,
                    width: '100px',
                    center: true,
                    cell: (row) => {
                      const isAllowedToEdit = getIsAllowedToEdit(row);
                      const hasPriceError = !discountIsPercentage && Number(row.PrecoReal || 0) < maxDiscountBrl;
                      const tooltipText = hasPriceError
                        ? 'O valor do produto é menor que o desconto progressivo'
                        : !isAllowedToEdit
                        ? 'Este produto já possui outro desconto progressivo ativo'
                        : undefined;

                      return (
                        <div
                          className="check-trail-module"
                          title={tooltipText}
                          style={{
                            cursor: isAllowedToEdit ? 'pointer' : 'not-allowed',
                            opacity: isAllowedToEdit ? 1 : 0.4,
                          }}
                          onClick={() => {
                            if (!isAllowedToEdit) return;
                            const prodId = row.IdProduto !== undefined ? row.IdProduto : (row.idProduto !== undefined ? row.idProduto : row.id);
                            const secaoId = row.IdSecao !== undefined ? row.IdSecao : (row.idSecao !== undefined ? row.idSecao : 0);
                            handleProductToggleLink(prodId, secaoId, row._rowId);
                          }}
                        >
                          {row.Vinculado ? (
                            <RiCheckboxFill size={25} color={isAllowedToEdit ? colors.success : '#ccc'} />
                          ) : (
                            <RiCheckboxBlankLine size={25} color={isAllowedToEdit ? undefined : '#ccc'} />
                          )}
                        </div>
                      );
                    },
                  },
                ]}
                columnFilter={['Nome', 'NomeSecao', 'IdSecao', 'IdProduto', 'NomeDescontoProgressivo']}
                isLoading={productsIsLoading || discountIsLoading}
              />
            </div>

            <Row style={{ marginTop: '2rem', justifyContent: 'flex-end' }}>
              <Col xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  {translate('save') || 'Salvar'}
                </button>
              </Col>
            </Row>
          </S.Container>
        )}
      </Card>
    </>
  );
}

export default ProgressiveDiscountCreate;
